import 'CssFolder/monitoring-report/file-level.scss';
import { Dialog } from "construct-ui";
import moment from "moment";
import DatePickerComponent from '../../../components/date-picker/date-picker.component';
import MultiSelectComponent from '../../../components/multi-select/multi-select.component';
import paginationComponent from '../../../components/pagination/pagination.component';
import { formats as FORMATS } from "../../../config/constants";
import MonitoringReportFileContentModel from '../../../models/monitoring-report/file-level.model';
import IMEIBasedSuccessRateModel from '../../../models/reports/imei-based-success-rate.model';
import ViewBase from '../../view-base.view';
import SuccessfulFileDetails from "./successful-file-details.view";

/**
 * [INTF] Monitoring Report - File Level
 *
 *
 * @author cmc.kmduc1(Henry)
 */

const DATA_BY_STATUS = {
  RAW_DATA: '',
  SUCCESSFUL_DATA: 'SUCCESS',
  FAILED_DATA: 'FAILED'
}

const STATUS_FILE_DOWNLOAD = {
  SUCCESS: 0,
  NEW_UPLOAD: 1,
  FAIL: 2
}

export default function MonitoringReportFileLevelView() {
  ViewBase.call(this);

  const DEFAULT_FILTERS = Object.freeze({
    clientCodes: [],
    fileTypes: [],
    fromDate: moment().format(FORMATS.ISO_DATE_FORMAT),
    toDate: moment().format(FORMATS.ISO_DATE_FORMAT),
    includeUnknown: false
  });

  const DEFAULT_PAGINATION = Object.freeze({
    pageIndex: 1,
    pageSize: 10,
    totalCount: 0
  });

  let successfulFileDetailProps = {
    customerCode: '',
    customerName: '',
    fromDate: '',
    toDate: '',
    fileType: '',
    fileCode: ''
  }

  let customers = [];
  let fileTypes = [];

  let filters = structuredClone(DEFAULT_FILTERS);
  let pagination = structuredClone(DEFAULT_PAGINATION);
  let currentFilters = structuredClone(DEFAULT_FILTERS);

  let tableData = [];

  let isOpenSuccessfulFileDetails = false;
  let isShowTable = false;

  const getCustomers = () => IMEIBasedSuccessRateModel.getCustomers()
    .then(result => {
      customers = result
    })
    .catch(error => {
      this.handleError(error)
    })

  const getFileTypes = () => MonitoringReportFileContentModel.getIntfFileType()
    .then(result => {
      fileTypes = result
    })
    .catch(error => {
      this.handleError(error)
    })

  const handleClear = () => {
    filters = structuredClone(DEFAULT_FILTERS);
    pagination = structuredClone(DEFAULT_PAGINATION);
    currentFilters = structuredClone(DEFAULT_FILTERS);
    tableData = [];
    isShowTable = false;
  };

  const handleSearchClick = () => {
    isShowTable = true;
    currentFilters = structuredClone(filters);
    pagination.pageIndex = 1;

    getMonitoringReportFileLevel();
  }

  const getMonitoringReportFileLevel = () => {
    this.showLoading();

    const params = {
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      clientCode: currentFilters.clientCodes.map(clientCode => clientCode.code),
      fileType: currentFilters.fileTypes.map(fileType => fileType.code),
      fromDate: currentFilters.fromDate,
      toDate: currentFilters.toDate,
      includeUnknown: currentFilters.includeUnknown ? 'Y' : 'N',
      sortBy: ''
    }

    MonitoringReportFileContentModel.intfFileLevelReport(params)
      .then((result) => {
        tableData = result.data;
        pagination.pageIndex = result.pageIndex;
        pagination.pageSize = result.pageSize;
        pagination.totalItems = result.total;
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const handleDatePickerChange = (e) => {
    filters.fromDate = moment(e.detail.start).format(FORMATS.ISO_DATE_FORMAT);
    filters.toDate = moment(e.detail.end).format(FORMATS.ISO_DATE_FORMAT);
  }

  const handleDownloadRawDataByStatus = (status) => {
    this.showLoading();

    const fileDownloadNameMapping = {
      [DATA_BY_STATUS.RAW_DATA]: 'INTF-FileLevel-RawData_',
      [DATA_BY_STATUS.SUCCESSFUL_DATA]: 'INTF-FileLevel-Success_',
      [DATA_BY_STATUS.FAILED_DATA]: 'INTF-FileLevel-Failed _',
    }

    const params = {
      clientCode: currentFilters.clientCodes.map(clientCode => clientCode.code).join('^'),
      fromDate: currentFilters.fromDate,
      toDate: currentFilters.toDate,
      fileType: currentFilters.fileTypes.map(fileType => fileType.code).join('^'),
      status
    }

    MonitoringReportFileContentModel.intfFileLevelRawDataByStatus(params, `${fileDownloadNameMapping[status]}${moment().format('YYYYMMDDHHmm')}`)
      .catch((error) => this.handleError(error))
      .finally(() => {
        this.hideLoading();
      });
  }

  const handleDownloadFileLevelSummary = () => {
    this.showLoading();

    const params = {
      clientCode: currentFilters.clientCodes.map(clientCode => clientCode.code).join('^'),
      fromDate: currentFilters.fromDate,
      toDate: currentFilters.toDate,
      fileType: currentFilters.fileTypes.map(fileType => fileType.code).join('^'),
      includeUnknown: currentFilters.includeUnknown
    }
    MonitoringReportFileContentModel.intfFileLevelSummary(params, `INTF-FileLevel-SummaryReport_${moment().format('YYYYMMDDHHmm')}`)
      .catch((error) => this.handleError(error))
      .finally(() => {
        this.hideLoading();
      });
  }

  const handleDownloadFileLevelDateVSNoOfUploadedFiles = () => {
    this.showLoading();

    const params = {
      clientCode: currentFilters.clientCodes.map(clientCode => clientCode.code),
      fromDate: currentFilters.fromDate,
      toDate: currentFilters.toDate,
      fileType: currentFilters.fileTypes.map(fileType => fileType.code),
      includeUnknown: currentFilters.includeUnknown ? 'Y' : 'N'
    }
    MonitoringReportFileContentModel.intfFileLevelDateVSNoOfFiles(params, `INTF-FileLevel-DateVsNoOf_${moment().format('YYYYMMDDHHmm')}`)
      .catch((error) => this.handleError(error))
      .finally(() => {
        this.hideLoading();
      });
  }

  const handlePageIndexChange = (pageIndex) => {
    pagination.pageIndex = pageIndex;
    getMonitoringReportFileLevel();
  }

  const handlePageSizeChange = (pageIndex, pageSize) => {
    pagination.pageSize = pageSize;
    pagination.pageIndex = pageIndex;
    getMonitoringReportFileLevel();
  }

  const handleOpenFileLevelDetail = (params) => {
    if (params.customerCode != 'Unknown') {
      isOpenSuccessfulFileDetails = true;
      successfulFileDetailProps = {
        customerCode: params.customerCode,
        customerName: params.customerName,
        fromDate: currentFilters.fromDate,
        toDate: currentFilters.toDate,
        fileType: params.fileType,
        fileCode: params.fileCode
      }
    }
  }

  const handleFileDownload = (e, data, status) => {
    e.preventDefault()
    const params =
    {
      fromDate: currentFilters.fromDate,
      toDate: currentFilters.toDate,
      clientCode: data.customerCode,
      fileType: data.fileCode,
      status
    }

    MonitoringReportFileContentModel.intfFileLevelFileDownload(params, 'INTF-FileLevel-FileDownload')
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  }

  return {
    oninit: () => {
      getCustomers();
      getFileTypes();
    },

    view: () => {
      return [
        /* SEARCH */
        m(".card.mb-3",
          m(".card-body",
            m(".row.g-2",
              m(".col-xl-3.col-lg-4.col-sm-6.position-relative",
                m('label.form-label[for="customer"]',
                  this.getText("LT00008046", "Customer")
                ),
                m(MultiSelectComponent, {
                  id: "customer",
                  items: customers,
                  allowEmpty: true,
                  selectedItems: filters.clientCodes,
                  onSelect: (selectedItems) => {
                    filters.clientCodes = selectedItems;
                  },
                })
              ),
              m(".col-xl-3.col-lg-4.col-sm-6.position-relative",
                m('label.form-label[for="fileType"]',
                  this.getText("LT00008055", "File Type")
                ),
                m(MultiSelectComponent, {
                  id: "fileType",
                  items: fileTypes,
                  allowEmpty: true,
                  selectedItems: filters.fileTypes,
                  onSelect: (selectedItems) => {
                    filters.fileTypes = selectedItems;
                  },
                })
              ),
              m(".col-xl-3.col-lg-4.col-sm-6.position-relative",
                m("label.form-label[for=process-date]",
                  this.getText("LT00008056", "Process Date")
                ),
                m(DatePickerComponent, {
                  id: 'process-date',
                  class: 'form-control',
                  isRange: true,
                  minDays: 1,
                  dateStart: filters?.fromDate,
                  dateEnd: filters?.toDate,
                  onselect: (e) => {
                    handleDatePickerChange(e);
                  }
                })
              ),
              m('.col-xl-3.col-lg-4.col-sm-6.position-relative.d-flex.align-items-center',
                m('input#includeUnknown.form-check-input.me-2', {
                  type: 'checkbox',
                  checked: filters.includeUnknown,
                  onchange: (e) => {
                    filters.includeUnknown = e.target.checked;
                  }
                },),
                m('label.form-check-label[for="includeUnknown"]', this.getText('LT00001025', 'Include Unknown'))
              ),
              m(".col-12.d-flex.justify-content-end.gap-2",
                m("button.btn.btn-primary",
                  {
                    type: "submit",
                    onclick: handleSearchClick
                  },
                  this.getText("LT00000001", "Search")
                ),
                m("button.btn.btn-white",
                  {
                    type: "button",
                    onclick: handleClear

                  },
                  this.getText("LT00000031", "Clear")
                )
              )
            )
          )
        ),

        /* TABLE */
        isShowTable &&
        m(".card",
          m(".card-body",
            m(".row.g-2",
              m(".col-12.d-flex.justify-content-end",
                m(".d-flex.gap-2",
                  m('.input-group.w-auto',
                    m('button.btn.btn-primary.dropdown-toggle.dropdown-toggle-split[type=button][data-bs-toggle=dropdown][arie-expanded=false]',
                      m('i.bi.bi-file-earmark-arrow-down-fill'),
                      this.getText("LT00000492", "Download")
                    ),
                    m('ul.dropdown-menu',
                      m('li',
                        m('a.dropdown-item[href="javascript:void(0);"]',
                          {
                            onclick: () => { handleDownloadRawDataByStatus(DATA_BY_STATUS.RAW_DATA) }
                          },
                          this.getText("LT00008050", "Raw Data")
                        )
                      ),
                      m('li',
                        m('a.dropdown-item[href="javascript:void(0);"]',
                          {
                            onclick: () => { handleDownloadRawDataByStatus(DATA_BY_STATUS.SUCCESSFUL_DATA) }
                          },
                          this.getText("LT00001033", "Successful Data")
                        )
                      ),
                      m('li',
                        m('a.dropdown-item[href="javascript:void(0);"]',
                          {
                            onclick: () => { handleDownloadRawDataByStatus(DATA_BY_STATUS.FAILED_DATA) }
                          },
                          this.getText("LT00001034", "Failed Data")
                        )
                      ),
                      m('li',
                        m('a.dropdown-item[href="javascript:void(0);"]',
                          {
                            onclick: handleDownloadFileLevelSummary
                          },
                          this.getText("LT00008049", "Summary Report")
                        )
                      ),
                      m('li',
                        m('a.dropdown-item[href="javascript:void(0);"]',
                          {
                            onclick: handleDownloadFileLevelDateVSNoOfUploadedFiles
                          },
                          this.getText("LT00001035", "Date vs No. of uploaded files")
                        )
                      ),
                    ),
                  ),
                )
              ),
              m(".col-12",
                m(".table-responsive",
                  m("table.table.monitoring-report.table-hover",
                    m("thead",
                      m("tr.align-middle.text-center",
                        m('th[scope="col"]',
                          this.getText("LT00001026", "Customer Code")
                        ),
                        m('th[scope="col"]',
                          this.getText("LT00001027", "Customer Name")
                        ),
                        m('th[scope="col"]',
                          this.getText("LT00008055", "File Type")
                        ),
                        m('th[scope="col"]',
                          this.getText("LT00008060", "Total Files")
                        ),
                        m('th[scope="col"]',
                          this.getText("EM00000004", "Success")
                        ),
                        m('th[scope="col"]',
                          this.getText("LT00001028", "New Upload")
                        ),
                        m('th[scope="col"]',
                          this.getText("EM00000036", "Fail")
                        ),
                        m('th.file-level-col__border--left.fst-italic.fw-light[scope="col"]',
                          this.getText("LT00001029", "Invalid File Name")
                        ),
                        m('th.fst-italic.fw-light[scope="col"]',
                          this.getText("LT00001030", "Invalid Client Code")
                        ),
                        m('th.fst-italic.fw-light[scope="col"]',
                          this.getText("LT00001031", "Non-Encryption")
                        ),
                        m('th.fst-italic.fw-light[scope="col"]',
                          this.getText("LT00001032", "System Exception")
                        )
                      )
                    ),
                    m("tbody",
                      tableData.length == 0 &&
                      m("tr.align-middle",
                        m("td[colSpan=13]",
                          this.getText("EM00000141", "No records found.")
                        )
                      ),
                      tableData.length > 0 &&
                      tableData.map((data, index) =>
                        m("tr.align-middle.text-center",
                          m("td", m("span.cursor-text", data.customerCode)),
                          m("td", m("span.cursor-text", data.customerName)),
                          m("td", m("span.cursor-text", data.fileType)),
                          m("td", m("span.cursor-text", data.totalFiles)),
                          m("td", {
                            onclick: () => {
                              handleOpenFileLevelDetail(data)
                            }
                          }, m(`span${data.customerCode == 'Unknown' ? '.cursor-text' : '.cursor-pointer.text-primary'}`, data.success)),
                          m('td',
                            data.newUpload > 0
                              ? m('a.btn.btn-link.p-0',
                                {
                                  href: '#',
                                  onclick: (e) => { handleFileDownload(e, data, STATUS_FILE_DOWNLOAD.NEW_UPLOAD) },
                                  role: 'button'
                                }, data.newUpload)
                              : data.newUpload
                          ),
                          m('td',
                            data.fail > 0
                              ? m('a.btn.btn-link.p-0',
                                {
                                  href: '#',
                                  onclick: (e) => { handleFileDownload(e, data, STATUS_FILE_DOWNLOAD.FAIL) },
                                  role: 'button'
                                }, data.fail)
                              : data.fail
                          ),
                          m("td.file-level-col__border--left", m("span.cursor-text", data.invalidFileName)),
                          m("td", m("span.cursor-text", data.invalidClientCode)),
                          m("td", m("span.cursor-text", data.nonEncryption)),
                          m("td", m("span.cursor-text", data.systemException))
                        )
                      )
                    )
                  )
                ),
              ),
              m(
                ".col-12",
                m(paginationComponent, {
                  currentPage: pagination.pageIndex,
                  pageSize: pagination.pageSize,
                  totalItems: pagination.totalItems,
                  onPageChanged: handlePageIndexChange,
                  onPageSizeChanged: handlePageSizeChange
                })
              )
            )
          )
        ),

        /* SUCCESSFUL FILE DETAIL MODAL */
        m(Dialog, {
          isOpen: isOpenSuccessfulFileDetails,
          transitionDuration: 200,
          closeOnOutsideClick: false,
          onClose: () => { isOpenSuccessfulFileDetails = false },
          title: this.getText("LT00001036", "Successful File Details"),
          content: m(SuccessfulFileDetails,
            { data: successfulFileDetailProps }),
          footer: [
            m('.d-flex.justify-content-end',
              m("button.btn.btn-white",
                {
                  type: "button",
                  onclick: () => {
                    isOpenSuccessfulFileDetails = false;
                  },
                },
                this.getText("LT00000021", "Close")
              )
            )
          ],
          "class": "cui-dialog-xxl"
        }),
        this.getBaseViews(),
      ];
    },
  };
}
